// Stands in for a stretch of code: not whitespace, so a "/slug" right after code still isn't at a word boundary.
const CODE = "￼";

// A fence opens on a line of three or more backticks (plus an info string without backticks) and closes on a line of
// at least as many backticks and nothing else; an unclosed fence runs to the end, as Markdown reads it. A pasted Windows
// line keeps its carriage return, which doesn't count.
const OPENING_FENCE = /^ {0,3}(`{3,})[^`]*$/;
const CLOSING_FENCE = /^ {0,3}(`{3,})[ \t]*\r?$/;

// A line break anywhere, or a carriage return anywhere but at the end (where a Windows line keeps it).
const BREAKS_LINE = /\n|\r(?!$)/;

// The backticks of a line the fence rules read as an opening, or undefined, a value spanning lines included.
export const openingFence = (line: string) => (BREAKS_LINE.test(line) ? undefined : line.match(OPENING_FENCE)?.[1]);

export const closesFence = (line: string, fence: string) => !BREAKS_LINE.test(line) && (line.match(CLOSING_FENCE)?.[1].length ?? 0) >= fence.length;

const backtickRunEnd = (text: string, start: number) => {
    let end = start;
    while (text[end] === "`") {
        end++;
    }

    return end;
};

// Where the run of exactly `length` backticks that closes a span starts, or -1. Inside a span a backslash escapes
// nothing, so it can't hide the closer. A span stays on its line: the composer reads a message line by line, and
// sending must read code where the composer shows it.
const findCloser = (text: string, from: number, length: number) => {
    const lineEnd = text.indexOf("\n", from);
    for (let start = text.indexOf("`", from); start !== -1 && (lineEnd === -1 || start < lineEnd); start = text.indexOf("`", start)) {
        const end = backtickRunEnd(text, start);
        if (end - start === length) {
            return start;
        }
        start = end;
    }

    return -1;
};

export interface InlineCodeSpan {
    // Where the opening backticks start and the closing ones end, in the text.
    start: number;
    end: number;
    // How many backticks open and close it.
    ticks: number;
}

// The inline code spans of a text. A span runs from a backtick run to the next run of the same length; outside code, a
// backslash makes the next character, a backtick included, plain text.
export const findInlineCode = (text: string): InlineCodeSpan[] => {
    const spans: InlineCodeSpan[] = [];
    let index = 0;
    while (index < text.length) {
        if (text[index] === "\\") {
            index += 2;
            continue;
        }
        if (text[index] !== "`") {
            index++;
            continue;
        }
        const openerEnd = backtickRunEnd(text, index);
        const ticks = openerEnd - index;
        const closer = findCloser(text, openerEnd, ticks);
        if (closer === -1) {
            index = openerEnd;
        } else {
            spans.push({ start: index, end: closer + ticks, ticks });
            index = closer + ticks;
        }
    }

    return spans;
};

const withoutInlineCode = (text: string) => {
    let result = "";
    let cursor = 0;
    for (const { start, end } of findInlineCode(text)) {
        result += text.slice(cursor, start) + CODE;
        cursor = end;
    }

    return result + text.slice(cursor);
};

interface CodeBlockLines {
    // The indexes of the opening fence's line and the closing one's, which is the last line when the fence is unclosed.
    first: number;
    last: number;
    isClosed: boolean;
}

const findClosingFence = (lines: readonly string[], from: number, fence: string) => {
    for (let index = from; index < lines.length; index++) {
        if ((lines[index].match(CLOSING_FENCE)?.[1].length ?? 0) >= fence.length) {
            return index;
        }
    }

    return -1;
};

// A block's fence, made longer than any line of its code that would otherwise close it.
export const fenceAround = (codeLines: readonly string[], fence: string) => {
    const longest = Math.max(0, ...codeLines.map(line => line.match(CLOSING_FENCE)?.[1].length ?? 0));

    return longest >= fence.length ? "`".repeat(longest + 1) : fence;
};

// The fenced code blocks among a text's lines.
export const findCodeBlocks = (lines: readonly string[]): CodeBlockLines[] => {
    const blocks: CodeBlockLines[] = [];
    let index = 0;
    while (index < lines.length) {
        const fence = lines[index].match(OPENING_FENCE)?.[1];
        if (fence === undefined) {
            index++;
            continue;
        }
        const closing = findClosingFence(lines, index + 1, fence);
        const last = closing === -1 ? lines.length - 1 : closing;
        blocks.push({ first: index, last, isClosed: closing !== -1 });
        index = last + 1;
    }

    return blocks;
};

// The message with its code blocks and inline code each reduced to one placeholder, for reading what lies outside them.
export const withoutCode = (markdown: string): string => {
    const lines = markdown.split("\n");
    for (const { first, last } of findCodeBlocks(lines)) {
        lines.fill(CODE, first, last + 1);
    }

    return withoutInlineCode(lines.join("\n"));
};
